import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { getCurrentUser } from '../api/api';
import type { User } from '../types';
import { useApiErrorHandler } from './useApiErrorHandler';
import { useAuth } from '../context/authContext';

interface UseUserReturn {
    user: User | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<User | null>;
}

export const useUser = (): UseUserReturn => {
    const {
        user: authUser,
        isAuthenticated,
        isOptimistic,
        loading: authLoading,
    } = useAuth();
    const queryClient = useQueryClient();
    const [error, setError] = useState<Error | null>(null);

    // Sync AuthContext user to React Query cache
    useEffect(() => {
        if (authUser) {
            queryClient.setQueryData<User | null>(['user'], authUser);
        } else if (!isAuthenticated) {
            queryClient.removeQueries({ queryKey: ['user'], exact: true });
        }
    }, [authUser, isAuthenticated, queryClient]);

    // Use useQuery to properly subscribe to cache updates
    // This ensures components re-render when setQueryData is called
    // After Vite 6 + React 19 upgrade, getQueryData() doesn't trigger re-renders
    // We use a queryFn that can fetch, but configure it to only read from cache
    // React Query will subscribe to the query key and notify when setQueryData is called
    const { data: cachedUser, isLoading: isQueryLoading } = useQuery<User | null>({
        queryKey: ['user'],
        queryFn: async () => {
            // First try to read from cache
            const cached = queryClient.getQueryData<User | null>(['user']);
            if (cached) {
                return cached;
            }
            // If not in cache and authenticated, fetch (but this shouldn't happen due to enabled check)
            if (isAuthenticated) {
                return await getCurrentUser();
            }
            return null;
        },
        enabled: isAuthenticated, // Only subscribe when authenticated
        staleTime: Infinity, // Never consider stale - prefer cache
        gcTime: Infinity, // Keep in cache indefinitely
        refetchOnWindowFocus: false, // Don't refetch on window focus
        refetchOnReconnect: false, // Don't refetch on reconnect
        refetchOnMount: false, // Don't refetch on mount
        // Disable structural sharing to ensure re-renders on cache updates
        // After Vite 6 upgrade, React Query's structural sharing may prevent re-renders
        structuralSharing: false,
        // Use cached data as initial/placeholder to avoid unnecessary fetches
        initialData: () => queryClient.getQueryData<User | null>(['user']) ?? undefined,
        placeholderData: () => queryClient.getQueryData<User | null>(['user']) ?? undefined,
    });

    const refetch = useCallback(async (): Promise<User | null> => {
        if (!isAuthenticated) {
            setError(null);
            queryClient.removeQueries({ queryKey: ['user'], exact: true });
            return null;
        }

        setError(null);

        try {
            await queryClient.invalidateQueries({ queryKey: ['user'], exact: true });
            const freshUser = await queryClient.fetchQuery<User>({
                queryKey: ['user'],
                queryFn: getCurrentUser,
                staleTime: 0,
                gcTime: 10 * 60 * 1000,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            });

            queryClient.setQueryData<User | null>(['user'], freshUser);
            return freshUser;
        } catch (err: unknown) {
            const typedError = err instanceof Error ? err : new Error('Failed to fetch user data');
            setError(typedError);
            throw typedError;
        }
    }, [isAuthenticated, queryClient]);

    // Priority: authUser (from AuthContext) > cachedUser (from React Query)
    // AuthContext is the source of truth, but we subscribe to React Query for optimistic updates
    const resolvedUser = authUser ?? cachedUser ?? null;
    const isLoading = authLoading || isQueryLoading || (isAuthenticated && isOptimistic && !resolvedUser);

    return {
        user: resolvedUser,
        isLoading,
        error,
        refetch,
    };
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    const { handleApiError } = useApiErrorHandler();

    return useMutation({
        mutationFn: getCurrentUser,
        onSuccess: (updatedUser) => {
            // Update the user cache
            queryClient.setQueryData(['user'], updatedUser);
        },
        onError: (error) => {
            handleApiError(error, 'updateUser');
        }
    });
};
